import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Download, 
  FileText, 
  BarChart3, 
  TrendingUp, 
  ArrowLeft,
  Filter,
  RefreshCw,
  Mail,
  Phone,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  User
} from 'lucide-react';
import { 
  generateAndSaveModeratorReport, 
  generateCustomPeriodReport,
  getModeratorContacts, 
  getModeratorReports,
  generateModeratorReportPDF,
  exportReportToCSV,
  generateTrendData,
  getAllApplications
} from '../../services/moderatorService';
import { useAuthStore } from '../../stores/authStore';
import { CompanyContact, ModeratorReport, JobApplication } from '../../types';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, subWeeks, subMonths, subQuarters, subYears, isWithinInterval } from 'date-fns';
import { it } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

type TimeFilter = 'week' | 'month' | 'quarter' | 'year' | 'custom';
type ExportFormat = 'pdf' | 'excel' | 'csv';

interface ReportData {
  period: {
    start: Date;
    end: Date;
  };
  contacts: CompanyContact[];
  summary: {
    totalContacts: number;
    positiveResponses: number;
    negativeResponses: number;
    pendingResponses: number;
    noResponses: number;
  };
  comparison?: {
    previousPeriod: {
      totalContacts: number;
      positiveResponses: number;
      negativeResponses: number;
      pendingResponses: number;
      noResponses: number;
    };
    change: {
      totalContacts: number;
      positiveResponses: number;
      negativeResponses: number;
      pendingResponses: number;
      noResponses: number;
    };
  };
}

const COLORS = ['#10b981', '#ef4444', '#f59e0b', '#6b7280', '#3b82f6'];

const AdvancedReportPage: React.FC = () => {
  const { user, userProfile } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [allContacts, setAllContacts] = useState<CompanyContact[]>([]);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('week');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [historicalReports, setHistoricalReports] = useState<ModeratorReport[]>([]);
  const [candidateFilter, setCandidateFilter] = useState<string>('');
  const [applications, setApplications] = useState<JobApplication[]>([]);

  useEffect(() => {
    if (user?.uid && userProfile?.role === 'moderator') {
      loadData();
    }
  }, [user?.uid, userProfile?.role]);

  useEffect(() => {
    if (allContacts.length > 0) {
      generateReportData();
    }
  }, [allContacts, timeFilter, customStartDate, customEndDate]);

  const loadData = async () => {
    if (!user?.uid || userProfile?.role !== 'moderator') return;
    
    try {
      setLoading(true);
      console.log('🔄 Loading data for moderator:', user.uid);
      
      // Carica contatti e candidature in parallelo
      const [contacts, allApplications] = await Promise.all([
        getModeratorContacts(user.uid),
        getAllApplications()
      ]);
      
      console.log('📊 Loaded contacts:', contacts.length);
      console.log('📊 Loaded applications:', allApplications.length);
      
      setAllContacts(contacts);
      setApplications(allApplications);
      
      // Prova a caricare i report storici, ma non fallire se non ci riesce
      try {
        const reports = await getModeratorReports(user.uid);
        console.log('📊 Loaded reports:', reports.length);
        setHistoricalReports(reports);
      } catch (reportError) {
        console.warn('⚠️ Could not load historical reports:', reportError);
        setHistoricalReports([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (filter: TimeFilter): { start: Date; end: Date } => {
    const now = new Date();
    
    switch (filter) {
      case 'week':
        return {
          start: startOfWeek(now, { weekStartsOn: 1 }),
          end: endOfWeek(now, { weekStartsOn: 1 })
        };
      case 'month':
        return {
          start: startOfMonth(now),
          end: endOfMonth(now)
        };
      case 'quarter':
        return {
          start: startOfQuarter(now),
          end: endOfQuarter(now)
        };
      case 'year':
        return {
          start: startOfYear(now),
          end: endOfYear(now)
        };
      case 'custom':
        return {
          start: customStartDate ? new Date(customStartDate) : now,
          end: customEndDate ? new Date(customEndDate) : now
        };
      default:
        return {
          start: startOfWeek(now, { weekStartsOn: 1 }),
          end: endOfWeek(now, { weekStartsOn: 1 })
        };
    }
  };

  const getPreviousPeriod = (filter: TimeFilter): { start: Date; end: Date } => {
    const now = new Date();
    
    switch (filter) {
      case 'week':
        return {
          start: subWeeks(startOfWeek(now, { weekStartsOn: 1 }), 1),
          end: subWeeks(endOfWeek(now, { weekStartsOn: 1 }), 1)
        };
      case 'month':
        return {
          start: subMonths(startOfMonth(now), 1),
          end: subMonths(endOfMonth(now), 1)
        };
      case 'quarter':
        return {
          start: subQuarters(startOfQuarter(now), 1),
          end: subQuarters(endOfQuarter(now), 1)
        };
      case 'year':
        return {
          start: subYears(startOfYear(now), 1),
          end: subYears(endOfYear(now), 1)
        };
      default:
        return {
          start: subWeeks(startOfWeek(now, { weekStartsOn: 1 }), 1),
          end: subWeeks(endOfWeek(now, { weekStartsOn: 1 }), 1)
        };
    }
  };

  const generateReportData = () => {
    const currentPeriod = getDateRange(timeFilter);
    const previousPeriod = getPreviousPeriod(timeFilter);

    // Ottieni contatti filtrati (per candidato)
    const filteredContacts = getFilteredContacts();

    // Filtra contatti per periodo corrente
    const currentContacts = filteredContacts.filter(contact => {
      if (!contact.contactDate) return false;
      const contactDate = contact.contactDate.toDate();
      return isWithinInterval(contactDate, { start: currentPeriod.start, end: currentPeriod.end });
    });

    // Filtra contatti per periodo precedente (usa tutti i contatti per il confronto)
    const previousContacts = allContacts.filter(contact => {
      if (!contact.contactDate) return false;
      const contactDate = contact.contactDate.toDate();
      return isWithinInterval(contactDate, { start: previousPeriod.start, end: previousPeriod.end });
    });

    // Calcola statistiche periodo corrente
    const currentSummary = {
      totalContacts: currentContacts.length,
      positiveResponses: currentContacts.filter(c => c.response === 'positive').length,
      negativeResponses: currentContacts.filter(c => c.response === 'negative').length,
      pendingResponses: currentContacts.filter(c => c.response === 'pending').length,
      noResponses: currentContacts.filter(c => c.response === 'no-response').length
    };

    // Calcola statistiche periodo precedente
    const previousSummary = {
      totalContacts: previousContacts.length,
      positiveResponses: previousContacts.filter(c => c.response === 'positive').length,
      negativeResponses: previousContacts.filter(c => c.response === 'negative').length,
      pendingResponses: previousContacts.filter(c => c.response === 'pending').length,
      noResponses: previousContacts.filter(c => c.response === 'no-response').length
    };

    // Calcola variazioni
    const change = {
      totalContacts: currentSummary.totalContacts - previousSummary.totalContacts,
      positiveResponses: currentSummary.positiveResponses - previousSummary.positiveResponses,
      negativeResponses: currentSummary.negativeResponses - previousSummary.negativeResponses,
      pendingResponses: currentSummary.pendingResponses - previousSummary.pendingResponses,
      noResponses: currentSummary.noResponses - previousSummary.noResponses
    };

    setReportData({
      period: currentPeriod,
      contacts: currentContacts,
      summary: currentSummary,
      comparison: {
        previousPeriod: previousSummary,
        change
      }
    });
  };

  const handleGenerateReport = async () => {
    if (!user?.uid || !reportData || userProfile?.role !== 'moderator') return;
    
    try {
      setGeneratingReport(true);
      
      // Genera e salva il report usando il periodo selezionato
      let report: ModeratorReport;
      let periodStart: Date;
      let periodEnd: Date;
      
      if (timeFilter === 'custom' && customStartDate && customEndDate) {
        // Usa il periodo personalizzato
        periodStart = new Date(customStartDate);
        periodEnd = new Date(customEndDate);
        console.log('📊 Generazione report periodo personalizzato:', periodStart, 'a', periodEnd);
      } else {
        // Usa il periodo corrente calcolato
        const currentPeriod = getDateRange(timeFilter);
        periodStart = currentPeriod.start;
        periodEnd = currentPeriod.end;
        console.log('📊 Generazione report periodo:', timeFilter, periodStart, 'a', periodEnd);
      }
      
      report = await generateCustomPeriodReport(user.uid, periodStart, periodEnd);
      
      // Genera e scarica il PDF
      const moderatorName = user.displayName || user.email || 'Moderatore';
      await generateModeratorReportPDF(report, moderatorName);
      
      alert('Report generato e scaricato con successo!');
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Errore nella generazione del report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const exportToExcel = () => {
    if (!reportData) return;
    
    try {
      // Usa il periodo corretto dal reportData
      exportReportToCSV(reportData.contacts, reportData.period);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Errore nell\'esportazione del CSV');
    }
  };

  const getContactMethodIcon = (method: string) => {
    switch (method) {
      case 'Email': return <Mail className="w-4 h-4" />;
      case 'Telefono': return <Phone className="w-4 h-4" />;
      case 'LinkedIn': return <MessageSquare className="w-4 h-4" />;
      case 'Lettera': return <FileText className="w-4 h-4" />;
      case 'Form web': return <FileText className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getResponseColor = (response: string) => {
    switch (response) {
      case 'positive': return 'text-green-600 bg-green-100';
      case 'negative': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'no-response': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getResponseText = (response: string) => {
    switch (response) {
      case 'positive': return 'Positiva';
      case 'negative': return 'Negativa';
      case 'pending': return 'In Attesa';
      case 'no-response': return 'Nessuna Risposta';
      default: return 'N/A';
    }
  };

  // Funzione per ottenere il nome del candidato
  const getCandidateName = (contact: CompanyContact): string => {
    const application = applications.find(app => app.id === contact.applicationId);
    return application?.userName || 'Candidato non trovato';
  };

  // Funzione per filtrare i contatti
  const getFilteredContacts = () => {
    let filtered = allContacts;
    
    // Filtro per nome candidato
    if (candidateFilter.trim()) {
      filtered = filtered.filter(contact => {
        const candidateName = getCandidateName(contact);
        return candidateName.toLowerCase().includes(candidateFilter.toLowerCase());
      });
    }
    
    return filtered;
  };

  // Dati per i grafici
  const pieChartData = reportData ? [
    { name: 'Positiva', value: reportData.summary.positiveResponses, color: '#10b981' },
    { name: 'Negativa', value: reportData.summary.negativeResponses, color: '#ef4444' },
    { name: 'In Attesa', value: reportData.summary.pendingResponses, color: '#f59e0b' },
    { name: 'Nessuna', value: reportData.summary.noResponses, color: '#6b7280' }
  ].filter(item => item.value > 0) : [];

  const trendData = historicalReports.length > 0 ? generateTrendData(historicalReports.slice(-8)) : [];

  if (userProfile?.role !== 'moderator') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm w-full">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Accesso Negato</h3>
          <p className="text-gray-600 mb-6">Solo i moderatori possono accedere a questa pagina</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="w-full bg-blue-600 text-white rounded-xl py-3 px-4 font-medium hover:bg-blue-700 transition-colors"
          >
            Torna alla Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Caricamento...</h3>
          <p className="text-gray-600">Stiamo preparando il report avanzato</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Report Avanzato</h1>
                <p className="text-sm text-gray-600">Analisi dettagliata attività moderatore</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <Filter className="w-5 h-5" />
              </button>
              <button
                onClick={loadData}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Filtri */}
        {showFilters && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <h3 className="font-bold text-gray-900 mb-3">Filtri</h3>
            
            {/* Filtro Nome Candidato */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Nome Candidato</label>
              <input
                type="text"
                placeholder="Cerca per nome candidato..."
                value={candidateFilter}
                onChange={(e) => setCandidateFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Filtri Temporali */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-800 mb-2">Periodo Temporale</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                {[
                  { value: 'week', label: 'Settimana' },
                  { value: 'month', label: 'Mese' },
                  { value: 'quarter', label: 'Trimestre' },
                  { value: 'year', label: 'Anno' },
                  { value: 'custom', label: 'Personalizzato' }
                ].map(filter => (
                  <button
                    key={filter.value}
                    onClick={() => setTimeFilter(filter.value as TimeFilter)}
                    className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                      timeFilter === filter.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              
              {timeFilter === 'custom' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Inizio</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Fine</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Periodo Corrente e Filtri Attivi */}
        {reportData && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-900">Periodo Analizzato</h3>
              <span className="text-sm text-gray-600">
                {format(reportData.period.start, 'dd/MM/yyyy', { locale: it })} - {format(reportData.period.end, 'dd/MM/yyyy', { locale: it })}
              </span>
            </div>
            
            {/* Filtri Attivi */}
            {(candidateFilter.trim() || timeFilter !== 'week') && (
              <div className="pt-3 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Filtri Attivi:</h4>
                <div className="flex flex-wrap gap-2">
                  {candidateFilter.trim() && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Candidato: {candidateFilter}
                    </span>
                  )}
                  {timeFilter !== 'week' && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {timeFilter === 'custom' ? 'Periodo Personalizzato' : 
                       timeFilter === 'month' ? 'Mese' :
                       timeFilter === 'quarter' ? 'Trimestre' : 'Anno'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Statistiche Principali */}
        {reportData && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{reportData.summary.totalContacts}</div>
                <div className="text-sm text-gray-600">Contatti Totali</div>
                {reportData.comparison && (
                  <div className={`text-xs mt-1 ${
                    reportData.comparison.change.totalContacts > 0 ? 'text-green-600' : 
                    reportData.comparison.change.totalContacts < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {reportData.comparison.change.totalContacts > 0 ? '+' : ''}{reportData.comparison.change.totalContacts}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{reportData.summary.positiveResponses}</div>
                <div className="text-sm text-gray-600">Positive</div>
                {reportData.comparison && (
                  <div className={`text-xs mt-1 ${
                    reportData.comparison.change.positiveResponses > 0 ? 'text-green-600' : 
                    reportData.comparison.change.positiveResponses < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {reportData.comparison.change.positiveResponses > 0 ? '+' : ''}{reportData.comparison.change.positiveResponses}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{reportData.summary.negativeResponses}</div>
                <div className="text-sm text-gray-600">Negative</div>
                {reportData.comparison && (
                  <div className={`text-xs mt-1 ${
                    reportData.comparison.change.negativeResponses > 0 ? 'text-green-600' : 
                    reportData.comparison.change.negativeResponses < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {reportData.comparison.change.negativeResponses > 0 ? '+' : ''}{reportData.comparison.change.negativeResponses}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{reportData.summary.pendingResponses}</div>
                <div className="text-sm text-gray-600">In Attesa</div>
                {reportData.comparison && (
                  <div className={`text-xs mt-1 ${
                    reportData.comparison.change.pendingResponses > 0 ? 'text-green-600' : 
                    reportData.comparison.change.pendingResponses < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {reportData.comparison.change.pendingResponses > 0 ? '+' : ''}{reportData.comparison.change.pendingResponses}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">{reportData.summary.noResponses}</div>
                <div className="text-sm text-gray-600">Nessuna</div>
                {reportData.comparison && (
                  <div className={`text-xs mt-1 ${
                    reportData.comparison.change.noResponses > 0 ? 'text-green-600' : 
                    reportData.comparison.change.noResponses < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {reportData.comparison.change.noResponses > 0 ? '+' : ''}{reportData.comparison.change.noResponses}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Grafici - Nascosti temporaneamente */}
        {/* 
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <h3 className="font-bold text-gray-900 mb-4">Distribuzione Risposte</h3>
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-40 text-gray-500">
                <div className="text-center">
                  <BarChart3 className="h-10 w-10 mx-auto mb-2" />
                  <p className="text-sm">Nessun dato disponibile</p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <h3 className="font-bold text-gray-900 mb-4">Trend Temporale</h3>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="contatti" stroke="#3b82f6" strokeWidth={2} />
                  <Line type="monotone" dataKey="positive" stroke="#10b981" strokeWidth={2} />
                  <Line type="monotone" dataKey="negative" stroke="#ef4444" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-40 text-gray-500">
                <div className="text-center">
                  <TrendingUp className="h-10 w-10 mx-auto mb-2" />
                  <p className="text-sm">Nessun dato storico</p>
                  <p className="text-xs text-gray-400 mt-1">I trend verranno mostrati dopo aver generato alcuni report</p>
                </div>
              </div>
            )}
          </div>
        </div>
        */}

        {/* Azioni */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleGenerateReport}
              disabled={generatingReport}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              {generatingReport ? 'Generando...' : 'Scarica PDF'}
            </button>
            
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
            >
              <FileText className="w-4 h-4" />
              Esporta CSV
            </button>
          </div>
        </div>

        {/* Lista Contatti */}
        {reportData && reportData.contacts.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="font-bold text-gray-900 text-lg">Dettagli Contatti ({reportData.contacts.length})</h3>
              <p className="text-sm text-gray-600 mt-1">Contatti nel periodo selezionato</p>
            </div>
            
            <div className="divide-y divide-gray-100">
              {reportData.contacts.map((contact) => (
                <div key={String(contact.id || '')} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="space-y-4">
                    {/* Header con nome azienda e status */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-gray-900 text-lg truncate pr-2">
                          {contact.companyName}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Candidato: <span className="font-semibold text-gray-800">{getCandidateName(contact)}</span>
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap ${getResponseColor(String(contact.response || ''))}`}>
                        {getResponseText(String(contact.response || ''))}
                      </span>
                    </div>
                    
                    {/* Dettagli principali */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Calendar className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Data Contatto</p>
                          <p className="text-sm text-gray-900 font-semibold">
                            {contact.contactDate ? format(contact.contactDate.toDate(), 'dd/MM/yyyy HH:mm', { locale: it }) : 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          {getContactMethodIcon(contact.contactMethod)}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Metodo</p>
                          <p className="text-sm text-gray-900 font-semibold capitalize">
                            {contact.contactMethod}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Informazioni aggiuntive */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <User className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Contattato</p>
                          <p className="text-sm text-gray-900 font-semibold">
                            {contact.contactPerson || 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">ID Candidatura</p>
                          <p className="text-sm text-gray-900 font-mono">
                            {String(contact.applicationId || '').substring(0, 8)}...
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Note */}
                    {contact.notes && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 bg-blue-200 rounded-lg flex items-center justify-center mt-0.5">
                            <MessageSquare className="w-3 h-3 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-xs text-blue-700 font-medium mb-1">Note</p>
                            <p className="text-sm text-blue-800 leading-relaxed">{contact.notes}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messaggio Nessun Contatto */}
        {reportData && reportData.contacts.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessun contatto nel periodo</h3>
            <p className="text-gray-600 mb-4">
              Non ci sono contatti registrati per il periodo selezionato
            </p>
            <div className="text-sm text-gray-500 space-y-1">
              <p>• Contatti totali caricati: {allContacts.length}</p>
              <p>• Periodo analizzato: {format(reportData.period.start, 'dd/MM/yyyy', { locale: it })} - {format(reportData.period.end, 'dd/MM/yyyy', { locale: it })}</p>
              <p>• Filtro temporale: {timeFilter}</p>
            </div>
          </div>
        )}

        {/* Messaggio Nessun Dato */}
        {!loading && allContacts.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessun contatto trovato</h3>
            <p className="text-gray-600 mb-4">
              Non ci sono ancora contatti registrati per questo moderatore
            </p>
            <div className="text-sm text-gray-500 mb-4">
              <p>I contatti verranno mostrati qui dopo aver aggiunto contatti aziendali</p>
            </div>
            
            {/* Debug Info */}
            <details className="text-left bg-gray-50 rounded-lg p-4">
              <summary className="cursor-pointer font-medium text-gray-700 mb-2">Informazioni di debug</summary>
              <div className="text-xs text-gray-600 space-y-1">
                <p><strong>User ID:</strong> {user?.uid}</p>
                <p><strong>User Role:</strong> {userProfile?.role}</p>
                <p><strong>All Contacts:</strong> {allContacts.length}</p>
                <p><strong>Historical Reports:</strong> {historicalReports.length}</p>
                <p><strong>Time Filter:</strong> {timeFilter}</p>
                {allContacts.length > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 rounded">
                    <p className="font-medium text-blue-800 mb-1">Contatti disponibili:</p>
                    {allContacts.slice(0, 3).map((contact, index) => (
                      <p key={index} className="text-blue-700">
                        • {contact.companyName} - {contact.contactDate ? format(contact.contactDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A'}
                      </p>
                    ))}
                    {allContacts.length > 3 && <p className="text-blue-600">... e altri {allContacts.length - 3}</p>}
                  </div>
                )}
                {reportData && (
                  <>
                    <p><strong>Report Data Period:</strong> {format(reportData.period.start, 'dd/MM/yyyy', { locale: it })} - {format(reportData.period.end, 'dd/MM/yyyy', { locale: it })}</p>
                    <p><strong>Report Contacts:</strong> {reportData.contacts.length}</p>
                  </>
                )}
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedReportPage; 